import React from 'react';
import { toast } from 'react-toastify';
import { checkNetworkHealth } from '../../services/systemService';
import {
  CONNECTIVITY,
  getConnectivity,
  reportApiUnreachable,
  reportBrowserOffline,
  reportOnline,
  subscribeConnectivity,
} from '../../services/connectivityService';

/**
 * Navbar connectivity icon. Background polling is owned by ConnectivityProbe.
 */
class NetworkStatus extends React.Component {
  state = {
    isChecking: false,
    status: getConnectivity().status,
    lastChecked: getConnectivity().lastCheckedAt
      ? new Date(getConnectivity().lastCheckedAt)
      : null,
  };

  componentDidMount() {
    this.unsubscribe = subscribeConnectivity((next) => {
      this.setState({
        status: next.status,
        lastChecked: next.lastCheckedAt ? new Date(next.lastCheckedAt) : null,
      });
    });
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe();
  }

  checkHealth = async ({ silent = false } = {}) => {
    if (!navigator.onLine) {
      reportBrowserOffline();
      this.setState({ isChecking: false });
      if (!silent) toast.error('Network connection lost.');
      return;
    }

    this.setState({ isChecking: true });

    try {
      const { data: response } = await checkNetworkHealth();
      const isApiReachable =
        response === true || response?.status === true || response?.ok === true;

      if (isApiReachable) {
        reportOnline({ immediate: !silent });
        if (!silent) toast.success('Connection verified.');
      } else {
        reportApiUnreachable();
        if (!silent) toast.warning('API health check did not pass.');
      }
    } catch (error) {
      console.error('Error checking network health:', error);
      reportApiUnreachable();
      if (!silent) toast.error('API is unreachable.');
    } finally {
      this.setState({ isChecking: false });
    }
  };

  getStatusMeta() {
    const { isChecking, status, lastChecked } = this.state;
    const checked = lastChecked
      ? ` Last checked ${lastChecked.toLocaleTimeString()}.`
      : '';

    if (isChecking) {
      return {
        icon: 'fa fa-refresh fa-spin',
        color: 'text-info',
        label: `Checking connection...${checked}`,
      };
    }

    if (status === CONNECTIVITY.OFFLINE) {
      return {
        icon: 'fa fa-plug',
        color: 'text-danger',
        label: `Browser is offline.${checked}`,
      };
    }

    if (status === CONNECTIVITY.ONLINE) {
      return {
        icon: 'fa fa-podcast',
        color: 'text-success',
        label: `Connected to MBOSS API.${checked}`,
      };
    }

    return {
      icon: 'fa fa-exclamation-triangle',
      color: 'text-warning',
      label: `Browser is online, but MBOSS API is unreachable.${checked}`,
    };
  }

  render() {
    const status = this.getStatusMeta();
    return (
      <button
        type="button"
        className="network-status-button"
        onClick={() => this.checkHealth()}
        title={status.label}
        aria-label={status.label}
      >
        <i className={`${status.icon} navbar-icon ${status.color}`} />
      </button>
    );
  }
}

export default NetworkStatus;
